import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { CommonResult } from '../../../external/Common';
import { actions } from '../../modules/Actions';

export interface LoadingStatus {
  /**
   * 初期状態かどうか
   * 値が空のときなどに、初期状態かロード完了後かを判定するために必要
   */
  isInitial: boolean;
  /**
   * ローディング中かどうか
   */
  isLoading: boolean;
  result?: CommonResult;
  error?: any;
}

const initialLoadingStatus: LoadingStatus = {
  isInitial: true,
  isLoading: false,
};

/**
 * APIローディングの種類. LoadingStatusReducerのkey
 */
export type LoadingType =
  // ステータス別レポート
  | 'underNegotiation'
  | 'pendingNegotiation'
  | 'doneNegotiation'
  // 検索条件
  | 'conditions'
  // 検索結果
  | 'searchResult'
  // 顧客詳細
  | 'fetchCustomerDetail'
  | 'fetchActionHistory'
  | 'updateCustomerDetail'
  // TODO: 画面名称未確定のため、決まり次第変更
  // 顧客情報全表示
  | 'fetchCustomerInfoAll'
  // 商談詳細
  | 'negotiationDetail'
  // アクション登録／編集
  | 'fetchAction'
  | 'createAction'
  | 'updateAction'
  | 'deleteAction'
  // メール関連
  | 'fetchMailDetail'
  // 顧客登録／編集
  | 'fetchCustomer'
  | 'fetchCustomerList'
  | 'checkCustomer'
  | 'createCustomer'
  | 'updateCustomer'
  | 'deleteCustomer'
  | 'customerCreate'
  // 商談登録／編集
  | 'fetchNegotiation'
  | 'createNegotiation'
  | 'updateNegotiation'
  // 商品登録／編集
  | 'fetchProduct'
  | 'createProduct'
  | 'updateProduct'
  | 'deleteProduct'
  // ダッシュボードサマリー
  | 'summary'
  // ログイン
  | 'session'
  | 'mailTop'
  | 'category'
  | 'mailSend'
  //
  | 'mailAddressSelect'
  | 'mailTemplateList'
  //来店予約
  | 'visitReservation';

export const LOADING_TYPE = {
  UNDER_NEGOTIATION: 'underNegotiation' as LoadingType,
  PENDING_NEGOTIATION: 'pendingNegotiation' as LoadingType,
  DONE_NEGOTIATION: 'doneNegotiation' as LoadingType,
  CONDITIONS: 'conditions' as LoadingType,
  SEARCH_RESULT: 'searchResult' as LoadingType,
  FETCH_CUSTOMER_DETAIL: 'fetchCustomerDetail' as LoadingType,
  FETCH_CUSTOMER_INFO_ALL: 'fetchCustomerInfoAll' as LoadingType,
  FETCH_ACTION_HISTORY: 'fetchActionHistory' as LoadingType,
  UPDATE_CUSTOMER_DETAIL: 'updateCustomerDetail' as LoadingType,
  NEGOTIATION_DETAIL: 'negotiationDetail' as LoadingType,
  FETCH_ACTION: 'fetchAction' as LoadingType,
  CREATE_ACTION: 'createAction' as LoadingType,
  UPDATE_ACTION: 'updateAction' as LoadingType,
  DELETE_ACTION: 'deleteAction' as LoadingType,
  FETCH_MAIL_DETAIL: 'fetchMailDetail' as LoadingType,
  FETCH_CUSTOMER: 'fetchCustomer' as LoadingType,
  FETCH_CUSTOMER_LIST: 'fetchCustomerList' as LoadingType,
  CHECK_CUSTOMER: 'checkCustomer' as LoadingType,
  CREATE_CUSTOMER: 'createCustomer' as LoadingType,
  UPDATE_CUSTOMER: 'updateCustomer' as LoadingType,
  DELETE_CUSTOMER: 'deleteCustomer' as LoadingType,
  FETCH_NEGOTIATION: 'fetchNegotiation' as LoadingType,
  NEGOTIATION_CREATE: 'createNegotiation' as LoadingType,
  UPDATE_NEGOTIATION: 'updateNegotiation' as LoadingType,
  FETCH_PRODUCT: 'fetchProduct' as LoadingType,
  CREATE_PRODUCT: 'createProduct' as LoadingType,
  UPDATE_PRODUCT: 'updateProduct' as LoadingType,
  DELETE_PRODUCT: 'deleteProduct' as LoadingType,
  SUMMARY: 'summary' as LoadingType,
  SESSION: 'session' as LoadingType,
  MAIL_TOP: 'mailTop' as LoadingType,
  CATEGORY: 'category' as LoadingType,
  MAIL_SEND: 'mailSend' as LoadingType,
  MAIL_ADD_RESS_SELECT: 'mailAddressSelect' as LoadingType,
  MAIL_TEMP_LATE_LIST: 'mailTemplateList' as LoadingType,
  MAIL_OPT_IN_INFO: 'mailOptInInfo' as LoadingType,
  COMBI_ORDCSTM_INFO: 'combiOrdcstmInfo' as LoadingType,
  VISIT_RESERVATION: 'visitReservation' as LoadingType,
  REGISTERED_INFO_EXTERNAL_LINK: 'registeredInfoExternalLink' as LoadingType,
};

export type LoadingStatusReducerState = {
  [key in LoadingType]: LoadingStatus;
};

/**
 * APIローディング状態を管理するReducer
 *
 * LoadingStatusReducer = {
 *  underNegotiationPeriod: {
 *    isInitial: true,
 *    isLoading: false,
 *  },
 *  conditions: {
 *    isInitial: false,
 *    isLoading: false,
 *    error: { errorMsg: "..." }
 *  },
 *  ...
 * }
 */
export const initialLoadingStatusState: LoadingStatusReducerState = Object.values(LOADING_TYPE).reduce(
  (obj, key) => ({
    ...obj,
    [key]: initialLoadingStatus,
  }),
  {} as LoadingStatusReducerState
);

export const LoadingStatusReducer = reducerWithInitialState<LoadingStatusReducerState>(initialLoadingStatusState)
  .case(actions.refreshLoadingStatus, (state: LoadingStatusReducerState, loadingType: LoadingType) => {
    return {
      ...state,
      [loadingType]: initialLoadingStatus,
    };
  })
  .case(actions.updateLoadingStatus.started, (state: LoadingStatusReducerState, loadingType: LoadingType) => {
    return {
      ...state,
      [loadingType]: { isInitial: false, isLoading: true },
    };
  })
  .case(actions.updateLoadingStatus.done, (state: LoadingStatusReducerState, payload: { params: LoadingType; result?: CommonResult }) => {
    return {
      ...state,
      [payload.params]: {
        isInitial: false,
        isLoading: false,
        result: payload.result,
        error: payload.result?.resultMessageDtoList?.[0],
      },
    };
  })
  .case(actions.updateLoadingStatus.failed, (state: LoadingStatusReducerState, payload: { params: LoadingType; error: {} }) => {
    return {
      ...state,
      [payload.params]: {
        isInitial: false,
        isLoading: false,
        error: payload.error,
      },
    };
  });
